// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Rails, frames and patches.
 *
 * Ported from `rail_polys_nm` (`sigmaKi.py:318-371`). `rect` and `poly` are
 * literal geometry; `tb`, `lr` and `frame` are derived from the bounding box of
 * **everything placed before them**, which makes rails order-dependent — the
 * same rule as `panel.panelBBox()` in the build path.
 *
 * A frame is a ring: an outer rectangle with the inner area as a hole. That
 * matters beyond drawing, because the inner ring is a face tabs can bite.
 */

import { type BBox, bbox } from '../model/bbox.ts';
import type { Point } from '../model/geometry.ts';
import type { Polygon, Ring } from './outline.ts';
import { SpecError, type PatchEntry, type RailEntry } from './spec.ts';

export interface RailGeometry {
  polys: Polygon[];
  /** Break-off cuts the rail itself produces — frame corners only. */
  cuts: Point[][];
}

function rectRing(x0: number, y0: number, x1: number, y1: number): Ring {
  return [
    [x0, y0],
    [x1, y0],
    [x1, y1],
    [x0, y1],
  ];
}

function rectPolygon(x0: number, y0: number, x1: number, y1: number): Polygon {
  return { ext: rectRing(x0, y0, x1, y1), holes: [] };
}

function requireVec(value: unknown, where: string, what: string): [number, number] {
  if (!Array.isArray(value) || value.length < 2 ||
      typeof value[0] !== 'number' || typeof value[1] !== 'number') {
    throw new SpecError(`${where}: needs '${what}: [x, y]'`);
  }
  return [value[0], value[1]];
}

function num(value: unknown, fallback: number): number {
  return typeof value === 'number' ? value : fallback;
}

/**
 * Geometry for one `rails[]` entry.
 *
 * `bounds` is the union of everything placed so far, or null when nothing has
 * been placed yet — which the derived rail types cannot work from.
 */
export function railGeometry(rail: RailEntry, bounds: BBox | null, index: number): RailGeometry {
  const where = `rails[${index}]`;
  const type = rail.type;

  if (type === 'rect') {
    const [x, y] = requireVec(rail.at, where, 'at');
    const [w, h] = requireVec(rail.size, where, 'size');
    return { polys: [rectPolygon(x, y, x + w, y + h)], cuts: [] };
  }

  if (type === 'poly') {
    const points = rail.points;
    if (!Array.isArray(points) || points.length < 3) {
      throw new SpecError(`${where}: needs 'points: [[x, y], ...]' with at least three points`);
    }
    return {
      polys: [{ ext: points.map(([x, y]) => [x, y] as Point), holes: [] }],
      cuts: [],
    };
  }

  if (type !== 'tb' && type !== 'lr' && type !== 'frame') {
    throw new SpecError(
      `${where}: unknown rail type ${JSON.stringify(type)} (use rect | poly | tb | lr | frame)`,
    );
  }

  if (bounds === null) {
    throw new SpecError(`${where}: '${type}' rail needs at least one board placed before it`);
  }

  let { minX, minY, maxX, maxY } = bounds;

  if (type === 'tb' || type === 'lr') {
    const thickness = num(rail.thickness, 5);
    const space = num(rail.space, 0);

    if (type === 'tb') {
      if (rail.span !== undefined) {
        [minX, maxX] = requireVec(rail.span, where, 'span');
      }
      return {
        polys: [
          rectPolygon(minX, minY - space - thickness, maxX, minY - space),
          rectPolygon(minX, maxY + space, maxX, maxY + space + thickness),
        ],
        cuts: [],
      };
    }

    if (rail.span !== undefined) {
      [minY, maxY] = requireVec(rail.span, where, 'span');
    }
    return {
      polys: [
        rectPolygon(minX - space - thickness, minY, minX - space, maxY),
        rectPolygon(maxX + space, minY, maxX + space + thickness, maxY),
      ],
      cuts: [],
    };
  }

  const width = num(rail.width, 5);
  const hspace = num(rail.hspace, 3);
  const vspace = num(rail.vspace, 3);

  const ix0 = minX - hspace;
  const iy0 = minY - vspace;
  const ix1 = maxX + hspace;
  const iy1 = maxY + vspace;
  const ox0 = ix0 - width;
  const oy0 = iy0 - width;
  const ox1 = ix1 + width;
  const oy1 = iy1 + width;

  const ring: Polygon = {
    ext: rectRing(ox0, oy0, ox1, oy1),
    holes: [rectRing(ix0, iy0, ix1, iy1)],
  };

  const mode = rail.corner_cuts ?? 'none';
  const cuts: Point[][] = [];
  if (mode === 'both' || mode === 'v') {
    for (const x of [ix0, ix1]) cuts.push([[x, oy0], [x, iy0]]);
    for (const x of [ix0, ix1]) cuts.push([[x, iy1], [x, oy1]]);
  }
  if (mode === 'both' || mode === 'h') {
    for (const y of [iy0, iy1]) cuts.push([[ox0, y], [ix0, y]]);
    for (const y of [iy0, iy1]) cuts.push([[ix1, y], [ox1, y]]);
  }

  return { polys: [ring], cuts };
}

/** Patches are rails without cuts, and only the literal shapes. */
export function patchGeometry(patch: PatchEntry, index: number): Polygon[] {
  const type = patch.type ?? 'rect';
  if (type !== 'rect' && type !== 'poly') {
    throw new SpecError(`patches[${index}]: unknown type ${JSON.stringify(type)} (use rect | poly)`);
  }
  return railGeometry({ ...patch, type } as RailEntry, null, index).polys;
}

/** Union of the bounding boxes of some polygons. */
export function boundsOfPolygons(polys: Polygon[], seed: BBox | null = null): BBox | null {
  let out = seed;
  for (const poly of polys) {
    for (const [x, y] of poly.ext) {
      out = out === null
        ? bbox(x, y, x, y)
        : bbox(Math.min(out.minX, x), Math.min(out.minY, y), Math.max(out.maxX, x), Math.max(out.maxY, y));
    }
  }
  return out;
}
